import Card from '../../components/ui/Card';
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { scrapeRunsApi, type ScrapeRunDetail, type ScrapeRunSummary } from '../../api/scrapeRuns';

const RUN_TYPE_LABEL: Record<string, string> = {
  market_list: 'Marktliste',
  detail_monitor: 'Inserat-Überwachung',
  single_detail: 'Einzel-Detail',
  batch_detail: 'Detail-Batch',
  duplicate_scan: 'Duplikatsuche',
};

const STATUS_LABEL: Record<string, string> = {
  running: 'Läuft',
  completed: 'Abgeschlossen',
  completed_with_errors: 'Mit Fehlern',
  failed: 'Fehlgeschlagen',
  skipped_locked: 'Übersprungen (gesperrt)',
};

const LEVEL_LABEL: Record<string, string> = {
  error: 'Fehler',
  warning: 'Warnung',
  info: 'Hinweis',
};

const STATUS_CLASS: Record<string, string> = {
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  completed_with_errors: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  skipped_locked: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[status] || 'bg-gray-100 text-gray-700'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function fmt(dt: string | null): string {
  return dt ? new Date(dt).toLocaleString('de-DE') : '–';
}

function RunDetailRows({ runId }: { runId: number }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<ScrapeRunDetail>({
    queryKey: ['scrape-run', runId],
    queryFn: () => scrapeRunsApi.get(runId),
  });

  if (isLoading) return <div className="px-4 py-3 text-sm text-gray-500">Lädt…</div>;
  if (!data) return null;

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
      {data.error && (
        <div className="text-sm text-red-700">
          <span className="font-medium">Lauf-Fehler:</span> {data.error}
        </div>
      )}
      {data.entries.length === 0 && !data.error ? (
        <div className="text-sm text-gray-500">Keine Fehler in diesem Lauf.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-4 font-medium">Stufe</th>
              <th className="py-1 pr-4 font-medium">Message</th>
              <th className="py-1 font-medium">Listing ID</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id} className="border-t border-gray-200 align-top">
                <td className="py-1 pr-4">
                  <span className={`text-xs font-medium ${e.level === 'error' ? 'text-red-700' : e.level === 'warning' ? 'text-amber-700' : 'text-gray-600'}`}>
                    {LEVEL_LABEL[e.level] || e.level}
                  </span>
                </td>
                <td className="py-1 pr-4 text-gray-800 break-all">{e.message}</td>
                <td className="py-1 whitespace-nowrap">
                  {e.listing_id ? (
                    <button
                      onClick={() => navigate(`/inserat/${e.listing_id}`)}
                      className="btn btn-text"
                    >
                      #{e.listing_id} &rarr;
                    </button>
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface MetricColumn {
  label: string;
  value: (run: ScrapeRunSummary) => number;
  error?: boolean;
}

function RunTable({
  title,
  runs,
  metrics,
  expanded,
  onExpand,
  showType = false,
  emptyMessage,
}: {
  title: string;
  runs: ScrapeRunSummary[];
  metrics: MetricColumn[];
  expanded: number | null;
  onExpand: (runId: number) => void;
  showType?: boolean;
  emptyMessage?: string;
}) {
  if (runs.length === 0 && !emptyMessage) return null;
  const columnCount = 4 + metrics.length + (showType ? 1 : 0);

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-gray-800">{title}</h2>
      <Card className="overflow-hidden border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2 font-medium">Start</th>
              {showType && <th className="px-4 py-2 font-medium">Typ</th>}
              <th className="px-4 py-2 font-medium">Stadt</th>
              <th className="px-4 py-2 font-medium">Auslöser</th>
              <th className="px-4 py-2 font-medium">Status</th>
              {metrics.map((metric) => (
                <th key={metric.label} className="px-4 py-2 text-right font-medium">{metric.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr className="border-t border-gray-100">
                <td colSpan={columnCount} className="px-4 py-4 text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {runs.map((run) => (
              <Fragment key={run.id}>
                <tr
                  onClick={() => onExpand(run.id)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-2 whitespace-nowrap">{fmt(run.started_at)}</td>
                  {showType && <td className="px-4 py-2 whitespace-nowrap">{RUN_TYPE_LABEL[run.run_type] || run.run_type}</td>}
                  <td className="px-4 py-2 whitespace-nowrap">{run.city_name || (run.city_id ? `#${run.city_id}` : '–')}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{run.trigger === 'scheduler' ? 'Zeitplan' : 'Manuell'}</td>
                  <td className="px-4 py-2 whitespace-nowrap"><StatusBadge status={run.status} /></td>
                  {metrics.map((metric) => {
                    const value = metric.value(run);
                    return (
                      <td
                        key={metric.label}
                        className={`px-4 py-2 text-right ${metric.error ? `font-medium ${value > 0 ? 'text-red-700' : 'text-gray-400'}` : ''}`}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
                {expanded === run.id && (
                  <tr>
                    <td colSpan={columnCount} className="p-0">
                      <RunDetailRows runId={run.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </section>
  );
}

const ERROR_METRIC: MetricColumn = {
  label: 'Fehler',
  value: (run) => run.error_count || run.failed || 0,
  error: true,
};

export default function ScrapeLog() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const runsQuery = useQuery({
    queryKey: ['scrape-runs', statusFilter],
    queryFn: () => scrapeRunsApi.list({ status: statusFilter || undefined, limit: 100 }),
  });
  const duplicateRunsQuery = useQuery({
    queryKey: ['scrape-runs', 'duplicate-scan', statusFilter],
    queryFn: () => scrapeRunsApi.list({
      status: statusFilter || undefined,
      run_type: 'duplicate_scan',
      limit: 100,
    }),
  });
  const runs = runsQuery.data;
  const marketRuns = runs?.filter((run) => run.run_type === 'market_list') ?? [];
  const monitorRuns = runs?.filter((run) => run.run_type === 'detail_monitor') ?? [];
  const otherRuns = runs?.filter((run) => !['market_list', 'detail_monitor', 'duplicate_scan'].includes(run.run_type)) ?? [];
  const duplicateRuns = duplicateRunsQuery.data ?? [];
  const isLoading = runsQuery.isLoading || duplicateRunsQuery.isLoading;
  const isFetching = runsQuery.isFetching || duplicateRunsQuery.isFetching;

  const refetch = () => {
    runsQuery.refetch();
    duplicateRunsQuery.refetch();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold">Abruf-Protokoll</h1>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="btn btn-outline px-3 py-1.5"
        >
          {isFetching ? 'Aktualisiere…' : 'Aktualisieren'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Läufe der Markt-, Inserat- und Duplikat-Überwachung inkl. aufgetretener Fehler.
      </p>

      <div className="mb-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="">Alle Status</option>
          <option value="completed">Abgeschlossen</option>
          <option value="completed_with_errors">Mit Fehlern</option>
          <option value="failed">Fehlgeschlagen</option>
          <option value="skipped_locked">Übersprungen (Lock)</option>
          <option value="running">Läuft</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-gray-500">Lädt…</div>
      ) : (
        <div className="space-y-6">
          <RunTable
            title="Marktliste"
            runs={marketRuns}
            metrics={[
              { label: 'Geprüfte Inserate', value: (run) => run.checked },
              { label: 'Neu', value: (run) => run.created },
              { label: 'Aktualisiert', value: (run) => run.updated },
              ERROR_METRIC,
            ]}
            expanded={expanded}
            onExpand={(runId) => setExpanded(expanded === runId ? null : runId)}
            emptyMessage="Noch keine Duplikatsuchen protokolliert."
          />
          <RunTable
            title="Inserat-Überwachung"
            runs={monitorRuns}
            metrics={[
              { label: 'Geprüft', value: (run) => run.checked },
              { label: 'Online', value: (run) => run.online },
              { label: 'Offline', value: (run) => run.offline },
              ERROR_METRIC,
            ]}
            expanded={expanded}
            onExpand={(runId) => setExpanded(expanded === runId ? null : runId)}
          />
          <RunTable
            title="Duplikatsuche"
            runs={duplicateRuns}
            metrics={[
              { label: 'Geprüfte Inserate', value: (run) => run.checked },
              { label: 'Gefundene Duplikate', value: (run) => run.matched_duplicates },
              ERROR_METRIC,
            ]}
            expanded={expanded}
            onExpand={(runId) => setExpanded(expanded === runId ? null : runId)}
          />
          <RunTable
            title="Weitere Detail-Abrufe"
            runs={otherRuns}
            metrics={[
              { label: 'Geprüft', value: (run) => run.checked },
              ERROR_METRIC,
            ]}
            expanded={expanded}
            onExpand={(runId) => setExpanded(expanded === runId ? null : runId)}
            showType
          />
        </div>
      )}
    </div>
  );
}
